import React, { createContext, useContext, useEffect, useState, ReactNode, useRef, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { authAPI, userAPI, User } from '../services/api';
import { socketService } from '../services/socketService';
import analytics from '../services/analytics';

export interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  sessionToken: string | null;
  error: string | null;
  login: () => void;
  signup: (email: string, password: string, fullName: string, options?: { inviteToken?: string | null; signupIntent?: 'coach'; coachInviteToken?: string | null }) => Promise<{ verifyEmail: boolean; invited?: boolean; userType?: string | null; emailVerified?: boolean }>;
  emailLogin: (email: string, password: string, options?: { inviteToken?: string | null }) => Promise<{ ok: true; invited?: boolean; userType?: string | null; alreadyMember?: boolean }>;
  logout: () => Promise<void>;
  clearError: () => void;
  registerLogoutCallback: (callback: () => void) => void;
  /**
   * Freemium-redesign Phase 1 B4: push a fresh user object into context after
   * server-side state changes (specifically the POST /onboarding/quickstart
   * response). Without this, post-quickstart route guards read stale
   * user_type=null / family_id=null and misroute. Caller passes the full
   * response body matching GET /user/profile shape.
   */
  setUser: (updatedUser: User) => void;
  /**
   * Freemium-redesign Phase 1 D3: install a backend session token directly,
   * bypassing the Auth0 round-trip. Used by /email-verified after a successful
   * POST /auth/post-verify-session — at that point the backend has minted a
   * session keyed to the just-verified Auth0 user, and we'd rather drop the
   * user straight into /onboarding/quickStart than bounce them through Universal
   * Login (which previously dropped them on a password-prompt screen).
   *
   * Flips an internal guard so the Auth0 token-exchange effect does NOT clear
   * state on its next run (Auth0 isn't authenticated in this flow, so the
   * effect would otherwise overwrite the session with nulls).
   */
  setSessionDirectly: (sessionToken: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const {
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0IsLoading,
    user: auth0User,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const [state, setState] = useState({
    isAuthenticated: false,
    isLoading: true,
    user: null as User | null,
    sessionToken: null as string | null,
    error: null as string | null,
  });

  const logoutCallbacksRef = useRef<(() => void)[]>([]);

  // Set to true when setSessionDirectly() injects a backend-minted session
  // (post-verify-session flow). When set, the Auth0 token-exchange effect
  // must NOT tear down our state — Auth0 isn't authenticated on this path.
  // Cleared on logout so a subsequent fresh Auth0 login re-engages the
  // normal exchange.
  const directSessionRef = useRef(false);

  const registerLogoutCallback = (callback: () => void) => {
    logoutCallbacksRef.current.push(callback);
  };

  const clearError = () => setState(prev => ({ ...prev, error: null }));

  const login = () => {
    loginWithRedirect();
  };

  const signup = async (
    email: string,
    password: string,
    fullName: string,
    options?: { inviteToken?: string | null; signupIntent?: 'coach'; coachInviteToken?: string | null },
  ): Promise<{ verifyEmail: boolean; invited?: boolean; userType?: string | null; emailVerified?: boolean }> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // Create user account via your backend API. When an inviteToken is
      // passed, the backend skips email verification (the inviter is the
      // verification), auto-accepts the invitation, and returns a session
      // token so we can sign the user straight in.
      const response = await fetch('https://apsrecruiting.io/api/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          name: fullName,
          invite_token: options?.inviteToken || undefined,
          // Coach mode (?coach=1): the backend sends the coach verify email.
          signup_intent: options?.signupIntent || undefined,
          // Coach invite link (KGH-795). When it is open and matches the email,
          // the backend skips verification and answers with a sessionToken,
          // exactly like a family invite. Otherwise it is a normal coach signup.
          coach_invite_token: options?.coachInviteToken || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Provider-aware error: "code" is set when the email is already registered
        // under a specific provider. The SignUpPage uses the message verbatim.
        if (errorData.code === 'EMAIL_EXISTS_DIFFERENT_PROVIDER' || errorData.code === 'EMAIL_EXISTS_SAME_PROVIDER') {
          const err: any = new Error(errorData.message || 'An account with this email already exists');
          err.code = errorData.code;
          err.primaryProvider = errorData.primaryProvider;
          err.primaryProviderLabel = errorData.primaryProviderLabel;
          throw err;
        }
        throw new Error(errorData.error || errorData.message || 'Signup failed');
      }

      const signupResponse = await response.json();

      // Invited path: backend returned a sessionToken. Install it directly
      // (same as /auth/login flow) so the user lands authenticated with
      // no verify-email detour.
      if (signupResponse.success && signupResponse.sessionToken) {
        await setSessionDirectly(signupResponse.sessionToken);
        setState(prev => ({ ...prev, isLoading: false }));
        return {
          verifyEmail: false,
          invited: true,
          userType: signupResponse.user_type || null,
        };
      }

      if (signupResponse.success && signupResponse.user) {
        // Organic path: account created, verification email sent. Caller
        // shows the "check your inbox" screen. emailVerified is true only when
        // an invite created the account verified but the server could not
        // sign it in: no email is coming, so the caller signs in instead.
        setState(prev => ({ ...prev, isLoading: false }));
        return { verifyEmail: true, emailVerified: signupResponse.user?.email_verified === true };
      }
      throw new Error(signupResponse.message || 'Signup failed');
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage || 'Signup failed. Please try again.',
      }));
      throw error;
    }
  };

  /**
   * Direct email/password login that bypasses Auth0 Universal Login.
   * Posts to /auth/login (Resource Owner Password Grant under the hood),
   * receives a backend session token, then installs it via the same
   * setSessionDirectly path the verify-email flow uses. The result: the
   * collapsed-email login form doesn't have to bounce through Auth0's
   * hosted page at all. Social logins still go through loginWithRedirect.
   *
   * Throws on failure with .code populated when the backend returned a
   * structured EMAIL_EXISTS_DIFFERENT_PROVIDER (user signed up via
   * Google/Apple and is trying to use email/password) or
   * EMAIL_NOT_VERIFIED.
   */
  const emailLogin = async (
    email: string,
    password: string,
    options?: { inviteToken?: string | null },
  ): Promise<{ ok: true; invited?: boolean; userType?: string | null; alreadyMember?: boolean }> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const response = await fetch('https://apsrecruiting.io/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          invite_token: options?.inviteToken || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.code === 'EMAIL_EXISTS_DIFFERENT_PROVIDER') {
          const err: any = new Error(errorData.message || 'This email uses a different sign-in method.');
          err.code = errorData.code;
          err.primaryProvider = errorData.primaryProvider;
          err.primaryProviderLabel = errorData.primaryProviderLabel;
          setState(prev => ({ ...prev, isLoading: false }));
          throw err;
        }
        if (errorData.error === 'EMAIL_NOT_VERIFIED') {
          const err: any = new Error(errorData.message || 'Please verify your email first.');
          err.code = 'EMAIL_NOT_VERIFIED';
          err.email = errorData.email || email;
          setState(prev => ({ ...prev, isLoading: false }));
          throw err;
        }
        const msg = errorData.error || errorData.message || 'Invalid email or password';
        setState(prev => ({ ...prev, isLoading: false, error: msg }));
        throw new Error(msg);
      }

      const data = await response.json();
      if (!data.sessionToken) {
        const msg = 'Login response missing session token';
        setState(prev => ({ ...prev, isLoading: false, error: msg }));
        throw new Error(msg);
      }
      // Install the session via the same direct path the verify-email
      // flow uses — flips the guard so the Auth0 effect doesn't clobber.
      await setSessionDirectly(data.sessionToken);
      return {
        ok: true,
        invited: !!data.invite_accepted,
        userType: data.invite_user_type || null,
        alreadyMember: !!data.invite_already_member,
      };
    } catch (error: any) {
      // setState already updated above for the no-throw paths; rethrow
      // so the caller (LoginPage) can branch on err.code.
      throw error;
    }
  };

  const logout = async () => {
    // Call all registered logout callbacks
    logoutCallbacksRef.current.forEach(callback => callback());

    // Analytics: drop the identified user before we clear state. After reset()
    // subsequent events are anonymous until the next login's identify().
    try { analytics.reset(); } catch (e) {}

    // Clear the direct-session guard so a subsequent real Auth0 login
    // re-engages the normal token exchange path.
    directSessionRef.current = false;

    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      sessionToken: null,
      error: null,
    });

    // Clear stored tokens
    sessionStorage.removeItem('sessionToken');
    localStorage.removeItem('sessionToken');

    // Logout from Auth0
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  // Rehydrate a backend session from sessionStorage on mount.
  //
  // Why: the new collapsed-email login form on staging uses Resource Owner
  // Password Grant (server-to-server). Auth0 never gets to set a browser
  // SSO cookie, so on refresh `auth0IsAuthenticated` is false and the Auth0
  // exchange effect below would clear state — kicking the user back to the
  // login page even though their session is still valid.
  //
  // We write the sessionToken to sessionStorage on every login path; this
  // effect is the missing read. Runs once on mount. If a stored token still
  // validates against the backend, we install it directly and flip
  // `directSessionRef.current` so the Auth0 effect skips its teardown. If
  // the token is rejected (expired / revoked), we clear it and let the
  // Auth0 flow take over.
  //
  // OAuth users (Google / Apple) hit this path too — their token from a
  // prior session is still in sessionStorage — but they have an Auth0 SSO
  // cookie as a fallback, so even if rehydration fails the Auth0 exchange
  // recovers.
  useEffect(() => {
    const stored = sessionStorage.getItem('sessionToken');
    if (!stored) return;

    // Flip the guard SYNCHRONOUSLY before any await so the Auth0 exchange
    // effect (which fires on the same mount tick once auth0IsLoading flips)
    // doesn't race us and clear state.
    directSessionRef.current = true;

    (async () => {
      try {
        const userProfile = await userAPI.getProfile(stored);
        setState({
          isAuthenticated: true,
          isLoading: false,
          user: userProfile,
          sessionToken: stored,
          error: null,
        });
        try {
          analytics.identify(userProfile.id, {
            email: userProfile.email,
            name: userProfile.name,
            user_type: userProfile.user_type,
            family_id: userProfile.family_id,
            onboarding_complete: userProfile.onboarding_complete,
            is_billing_owner: userProfile.is_billing_owner,
            is_internal: !!userProfile.organization_id,
          });
        } catch (e) { /* analytics must never break auth */ }
      } catch (err) {
        // Token no longer valid — drop it and let the Auth0 flow run.
        console.warn('Stored sessionToken rejected by backend; falling through to Auth0', err);
        sessionStorage.removeItem('sessionToken');
        directSessionRef.current = false;
        setState(prev => ({
          ...prev,
          isAuthenticated: false,
          isLoading: false,
          user: null,
          sessionToken: null,
        }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Exchange Auth0 token for backend session token
  useEffect(() => {
    const exchangeTokens = async () => {
      console.log('🔐 Auth Check:', { auth0IsAuthenticated, auth0User: auth0User?.email });
      
      if (!auth0IsAuthenticated || !auth0User) {
        // Direct-session injection (post-verify-session) bypasses Auth0; the
        // state was set by setSessionDirectly and must not be cleared here.
        if (directSessionRef.current) {
          console.log('⏭️  Direct session active — skipping Auth0 exchange teardown');
          return;
        }
        console.log('❌ Not authenticated, clearing state');
        setState(prev => ({
          ...prev,
          isAuthenticated: false,
          isLoading: false,
          user: null,
          sessionToken: null,
        }));
        return;
      }

      try {
        console.log('🔄 Starting token exchange...');
        setState(prev => ({ ...prev, isLoading: true }));

        // Race getAccessTokenSilently against a 4s timeout. The SDK can hang
        // indefinitely when the browser holds a stale SSO cookie for a user
        // that's since been deleted from Auth0 (or after some Auth0 outages),
        // which used to wedge every page that gates on AuthContext.isLoading.
        // On timeout we treat it as "not authenticated" and let downstream
        // surfaces (login form, post-verify-session, etc.) take over.
        const AUTH0_SILENT_TIMEOUT_MS = 4000;
        const auth0Token = await Promise.race<string>([
          getAccessTokenSilently(),
          new Promise<string>((_, reject) =>
            setTimeout(
              () => reject(new Error('AUTH0_SILENT_TIMEOUT')),
              AUTH0_SILENT_TIMEOUT_MS
            )
          ),
        ]);
        console.log('✅ Got Auth0 token');
        
        // Invite token: URL params for the email/password path that lands
        // here with ?invite=… still in the URL, localStorage for the
        // social-auth path where Auth0's redirect dance strips query
        // params before we re-mount. Without the localStorage fallback,
        // Google/Apple signups via invite link never get auto-accepted
        // (backend sees invite_token=null on /auth/session, user.family_id
        // stays null, and the combined onboarding screen falls into the
        // "no family athletes" create-form branch).
        const urlParams = new URLSearchParams(window.location.search);
        // ?coach=1&invite=... is a college coach invite, not a family one.
        // It must never reach the family auto-accept.
        const urlInvite = urlParams.get('coach') === '1' ? null : urlParams.get('invite');
        const inviteToken = urlInvite || localStorage.getItem('invitation_token');
        if (inviteToken) console.log('🎟️ Found invite token:', inviteToken);

        // Use the /auth/session endpoint (same as mobile app uses)
        console.log('📡 Calling backend session endpoint...');
        const response = await fetch('https://apsrecruiting.io/api/auth/session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            access_token: auth0Token,
            id_token: auth0Token, // Web uses same token
            refresh_token: '',
            expires_in: 3600,
            invite_token: inviteToken,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Session API error:', errorText);
          
          // Try to parse the error as JSON to get specific error message
          try {
            const errorData = JSON.parse(errorText);
            if (errorData.error?.code === 'EMAIL_NOT_VERIFIED') {
              // Include email in the error for resend functionality
              const email = errorData.error?.email || '';
              throw new Error(`EMAIL_NOT_VERIFIED:${email}:Please verify your email address before signing in. Check your inbox for the verification link we sent you.`);
            }
            if (errorData.error?.code === 'EMAIL_EXISTS_DIFFERENT_PROVIDER') {
              // Social login (Google/Apple) but email already registered with another provider.
              // Format: EMAIL_EXISTS_DIFFERENT_PROVIDER:<primaryProvider>:<label>:<email>:<message>
              // Email is carried through so LoginPage can pass it as login_hint, letting the
              // user land on the correct Auth0 connection form with their address pre-filled.
              const primaryProvider = errorData.error?.primaryProvider || 'unknown';
              const label = errorData.error?.primaryProviderLabel || 'another method';
              const email = errorData.error?.email || '';
              const msg = errorData.error?.message || `This email is already registered using ${label}.`;
              throw new Error(`EMAIL_EXISTS_DIFFERENT_PROVIDER:${primaryProvider}:${label}:${email}:${msg}`);
            }
            throw new Error(errorData.error?.message || 'Authentication failed');
          } catch (parseError: any) {
            // If it's one of our structured custom errors, re-throw it
            if (parseError.message?.startsWith('EMAIL_NOT_VERIFIED:') ||
                parseError.message?.startsWith('EMAIL_EXISTS_DIFFERENT_PROVIDER:')) {
              throw parseError;
            }
            throw new Error('Authentication failed');
          }
        }

        const authData = await response.json();
        console.log('✅ Session response:', authData);
        
        const sessionToken = authData.sessionToken;
        if (!sessionToken) {
          throw new Error('No session token received');
        }

        // Fetch full user profile from database (includes deleted_at, family, etc.)
        console.log('📡 Fetching user profile...');
        const userProfile = await userAPI.getProfile(sessionToken);
        console.log('✅ User profile:', userProfile);

        setState({
          isAuthenticated: true,
          isLoading: false,
          user: userProfile, // Use database user, not Auth0 user
          sessionToken: sessionToken,
          error: null,
        });

        // Store session token
        sessionStorage.setItem('sessionToken', sessionToken);
        console.log('✅ Authentication complete!');

        // Tie all subsequent analytics events to this user. Traits land as Person
        // properties, not event properties (keeps PII out of event payloads).
        try {
          analytics.identify(userProfile.id, {
            email: userProfile.email,
            name: userProfile.name,
            user_type: userProfile.user_type,
            family_id: userProfile.family_id,
            onboarding_complete: userProfile.onboarding_complete,
            is_billing_owner: userProfile.is_billing_owner,
            is_internal: !!userProfile.organization_id, // flags staff/admin for filtering
          });
          analytics.track('login_success');
        } catch (e) { /* analytics must never break auth */ }

      } catch (error: any) {
        const isSilentTimeout = error?.message === 'AUTH0_SILENT_TIMEOUT';
        if (isSilentTimeout) {
          // Stale Auth0 cookie / silent-auth hang — log quietly and degrade
          // to "not authenticated" so the rest of the app can proceed. Don't
          // surface an error to the UI because it's not a user-facing failure.
          console.warn('Auth0 silent auth timed out; bailing to unauthenticated state');
          setState(prev => ({
            ...prev,
            isAuthenticated: false,
            isLoading: false,
            user: null,
            sessionToken: null,
            error: null,
          }));
          return;
        }
        console.error('❌ Token exchange error:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response,
          status: error.status
        });
        // Analytics: classify failure reason so funnels can distinguish
        // verification-pending vs provider-mismatch vs generic failures.
        try {
          const msg: string = error?.message || '';
          const reason = msg.startsWith('EMAIL_NOT_VERIFIED:')
            ? 'email_not_verified'
            : msg.startsWith('EMAIL_EXISTS_DIFFERENT_PROVIDER:')
              ? 'wrong_provider'
              : 'generic';
          analytics.track('login_failed', { reason });
        } catch (e) { /* never break auth */ }
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: error.message || 'Authentication failed',
        }));
      }
    };

    if (!auth0IsLoading) {
      exchangeTokens();
    }
  }, [auth0IsAuthenticated, auth0IsLoading, auth0User, getAccessTokenSilently]);

  // Setup socket listener for account deletion
  useEffect(() => {
    if (!state.sessionToken) return;

    const handleAccountDeleted = (data: any) => {
      console.log('🗑️ Account deleted event received:', data);
      // Force immediate logout
      logout();
    };

    const handleAccountReactivated = (data: any) => {
      console.log('✅ Account reactivated event received:', data);
      // Optionally refresh user data or show notification
      // For now, just log it - user can refresh to see changes
    };

    // Subscribe to socket events
    socketService.subscribe('account:deleted', handleAccountDeleted);
    socketService.subscribe('account:reactivated', handleAccountReactivated);

    // Cleanup on unmount or token change
    return () => {
      socketService.unsubscribe('account:deleted', handleAccountDeleted);
      socketService.unsubscribe('account:reactivated', handleAccountReactivated);
    };
  }, [state.sessionToken]);

  const setUser = (updatedUser: User) => {
    setState((prev) => ({ ...prev, user: updatedUser }));
  };

  const setSessionDirectly = useCallback(async (sessionToken: string) => {
    // Flip the guard first so any Auth0 effect re-runs (e.g. on its initial
    // isLoading→false transition) don't clobber the state we're about to set.
    directSessionRef.current = true;

    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      sessionStorage.setItem('sessionToken', sessionToken);
      const userProfile = await userAPI.getProfile(sessionToken);
      setState({
        isAuthenticated: true,
        isLoading: false,
        user: userProfile,
        sessionToken,
        error: null,
      });
      try {
        analytics.identify(userProfile.id, {
          email: userProfile.email,
          name: userProfile.name,
          user_type: userProfile.user_type,
          family_id: userProfile.family_id,
          onboarding_complete: userProfile.onboarding_complete,
          is_billing_owner: userProfile.is_billing_owner,
          is_internal: !!userProfile.organization_id,
        });
        analytics.track('login_success', { method: 'post_verify_direct' });
      } catch (e) { /* never break auth */ }
    } catch (error: any) {
      directSessionRef.current = false;
      sessionStorage.removeItem('sessionToken');
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error?.message || 'Failed to load profile',
      }));
      throw error;
    }
  }, []);

  const value: AuthContextType = {
    ...state,
    login,
    signup,
    emailLogin,
    logout,
    clearError,
    registerLogoutCallback,
    setUser,
    setSessionDirectly,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
